import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { MatSelectModule } from '@angular/material/select';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { usaPhoneValidator } from '../../shared/validators/phone.validator';
import { strictEmailValidator } from '../../shared/validators/email.validator';
import { standardNameValidator } from '../../shared/validators/name.validator';
import { usaLicensePlateValidator } from '../../shared/validators/license-plate.validator';
import { vehicleYearValidator } from '../../shared/validators/year.validator';
import {
  CUSTOMER_NAME_MAX_LENGTH,
  CUSTOMER_PHONE_MAX_LENGTH,
  sanitizeCustomerPhoneInput,
} from '../../shared/validators/customer-contact-field.rules';
import { LocationService } from '../../services/location.service';
import { LocationOption } from '../../shared/types';
import { CustomerDetailsModalComponent } from '../customer-details-modal/customer-details-modal.component';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions/modal-footer-actions.component';
import { CustomerService, CustomerData } from '../../services/customer.service';
import {
  runCustomerPhoneSearch,
  runCustomerNameSearch,
  clearCustomerSearchDropdowns,
  patchCustomerFormFromSearchResult,
  setupCustomerSearchSubscriptions,
} from '../../shared/utils/customer-search.util';
import { hideDropdownAfterDelay } from '../../shared/utils/dropdown.util';

@Component({
  selector: 'app-add-customer-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    CustomerDetailsModalComponent,
    ModalFooterActionsComponent,
  ],
  templateUrl: './add-customer-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-customer-modal.component.scss'],
})
export class AddCustomerModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() customerCreated = new EventEmitter<any>();

  customerForm!: FormGroup;
  locationOptions: LocationOption[] = [];
  isSubmitting = false;
  isLoadingLocations = false;
  duplicateCustomerError: string | null = null;
  duplicateEmailError: string | null = null;
  duplicatePhoneError: string | null = null;
  duplicateLicensePlateError: string | null = null;
  existingCustomerData: any = null;
  showCustomerDetails = false;
  isLoadingCustomerDetails = false;
  showVehicleFields = false; // Flag to show make, model, year fields when "Invalid license plate" error occurs
  private locationSubscription?: Subscription;

  // Name search properties
  nameSearchResults: any[] = [];
  showNameDropdown = false;
  isSearchingCustomersByName = false;
  private nameSearchSubject = new Subject<string>();

  // Phone search properties
  phoneSearchResults: any[] = [];
  showPhoneDropdown = false;
  isSearchingCustomers = false;
  readonly phoneMaxLength = CUSTOMER_PHONE_MAX_LENGTH;
  private phoneSearchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private messageService: NotificationService,
    private locationService: LocationService,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.loadLocations();
    this.setupSearchSubscriptions();
  }

  private setupSearchSubscriptions(): void {
    setupCustomerSearchSubscriptions(
      this.nameSearchSubject,
      this.phoneSearchSubject,
      this.destroy$,
      name => this.searchCustomersByName(name),
      phoneNumber => this.searchCustomersByPhone(phoneNumber)
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && this.customerForm) {
      // Clear form when modal opens or closes
      this.clearForm();
    }
  }

  /**
   * Initializes the reactive customerForm FormGroup with controls and validators used by the AddCustomerModal component.
   * @example
   * nitializeForm()
   * // returns undefined
   * @returns {void} Initializes this.customerForm on the component instance; does not return a value.
   **/
  initializeForm() {
    this.customerForm = this.fb.group({
      firstName: [
        '',
        [Validators.maxLength(CUSTOMER_NAME_MAX_LENGTH), this.conditionalStandardNameValidator()],
      ],
      lastName: [
        '',
        [Validators.maxLength(CUSTOMER_NAME_MAX_LENGTH), this.conditionalStandardNameValidator()],
      ],
      phone: [
        '',
        [Validators.maxLength(CUSTOMER_PHONE_MAX_LENGTH), this.conditionalPhoneValidator()],
      ],
      email: ['', [this.conditionalEmailValidator()]],
      licensePlate: ['', [Validators.required, usaLicensePlateValidator()]],
      make: [''],
      model: [''],
      year: [''],
    });
  }

  // Conditional validators that only validate if value is provided
  private conditionalStandardNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !control.value.toString().trim()) {
        return null; // Skip validation for empty values
      }
      return standardNameValidator()(control);
    };
  }

  private conditionalPhoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !control.value.toString().trim()) {
        return null; // Skip validation for empty values
      }
      return usaPhoneValidator()(control);
    };
  }

  private conditionalEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !control.value.toString().trim()) {
        return null; // Skip validation for empty values
      }
      return strictEmailValidator()(control);
    };
  }

  /**
   * Reset the add-customer form and all related UI, validation, and component state.
   * @example
   * learForm()
   * undefined
   * @returns {void} Resets the form controls, validation states, search results, UI flags, and vehicle field validators; returns nothing.
   **/
  clearForm() {
    if (this.customerForm) {
      this.customerForm.reset();
      // Clear all validation states
      Object.keys(this.customerForm.controls).forEach(key => {
        this.customerForm.get(key)?.markAsUntouched();
        this.customerForm.get(key)?.markAsPristine();
      });
      // Reset submitting state and clear duplicate errors
      this.isSubmitting = false;
      this.clearAllDuplicateErrors();
      // Clear search results and hide dropdowns
      this.nameSearchResults = [];
      this.showNameDropdown = false;
      this.phoneSearchResults = [];
      this.showPhoneDropdown = false;
      // Reset customer details modal state
      this.showCustomerDetails = false;
      this.isLoadingCustomerDetails = false;
      // Reset vehicle fields visibility
      this.showVehicleFields = false;
      // Reset vehicle fields validators
      this.updateVehicleFieldsValidators();
    }
  }

  onClose() {
    if (!this.isSubmitting) {
      this.closeModal.emit();
    }
  }

  onClear() {
    if (!this.isSubmitting) {
      this.clearForm();
    }
  }

  /**
   * Validate and submit the customer form: required license plate must be valid, optional fields with values are validated, and a CustomerData object is emitted on success or validation messages are shown on failure.
   * @example
   * nSubmit()
   * void
   * @returns {void} Emits customerCreated with CustomerData on successful validation; otherwise shows validation error messages and marks invalid controls as touched.
   */
  onSubmit() {
    // Only license plate is mandatory, so check if it's valid
    const licensePlateControl = this.customerForm.get('licensePlate');
    const isLicensePlateValid = licensePlateControl?.valid;

    // Check if any optional fields with values are invalid
    const optionalFieldsWithErrors: string[] = [];
    const optionalFieldNames = ['firstName', 'lastName', 'phone', 'email', 'make', 'model', 'year'];

    optionalFieldNames.forEach(fieldName => {
      const control = this.customerForm.get(fieldName);
      if (control && control.value && control.value.toString().trim() && control.invalid) {
        optionalFieldsWithErrors.push(fieldName);
        control.markAsTouched();
      }
    });

    // Allow submission if license plate is valid and all fields with values are valid
    const canSubmit =
      isLicensePlateValid && optionalFieldsWithErrors.length === 0 && !this.isSubmitting;

    if (canSubmit) {
      this.isSubmitting = true;
      const formValue = this.customerForm.value;
      // Map form values to CustomerData structure
      const customerData: CustomerData = {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        phone: formValue.phone,
        email: formValue.email,
        licensePlate: formValue.licensePlate,
        customerType: 'regular',
        make: formValue.make || undefined,
        model: formValue.model || undefined,
        year: formValue.year || undefined,
      };

      this.customerCreated.emit(customerData);
    } else {
      // Mark license plate as touched to show validation errors
      if (!isLicensePlateValid) {
        licensePlateControl?.markAsTouched();
      }

      // Mark other fields that have values as touched to show their validation errors
      optionalFieldsWithErrors.forEach(fieldName => {
        const control = this.customerForm.get(fieldName);
        control?.markAsTouched();
      });

      if (!isLicensePlateValid) {
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: this.getFieldError('licensePlate') || 'License Plate is required',
          life: 3000,
        });
      } else if (optionalFieldsWithErrors.length > 0) {
        const fieldDisplayNames = optionalFieldsWithErrors
          .map(f => this.getFieldDisplayName(f))
          .join(', ');
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: `Please fix the following fields: ${fieldDisplayNames}`,
          life: 3000,
        });
      }
    }
  }

  // Called from parent component to reset form state
  resetSubmittingState() {
    this.isSubmitting = false;
  }

  // Called from parent component to set duplicate customer error
  /**
   * Clear previous duplicate errors and set the appropriate duplicate error state based on the provided error message, optionally storing existing customer data and adjusting vehicle field visibility/validators for license plate errors.
   * @example
   * etDuplicateCustomerError("Email already exists", { id: 123 })
   * undefined
   * @param {{string}} {{errorMessage}} - The error message returned from validation or backend used to determine which duplicate error field to set.
   * @param {{any}} {{existingCustomerData}} - Optional object containing existing customer data related to the duplicate for reference or display.
   * @returns {{void}} Void; the function updates component state and does not return a value.
   **/
  setDuplicateCustomerError(errorMessage: string, existingCustomerData?: any) {
    this.clearAllDuplicateErrors();

    // Determine which field has the duplicate based on the error message
    const messageLower = errorMessage.toLowerCase();

    if (messageLower.includes('email')) {
      this.duplicateEmailError = errorMessage;
    } else if (messageLower.includes('phone')) {
      this.duplicatePhoneError = errorMessage;
    } else if (messageLower.includes('license plate')) {
      if (errorMessage === 'Invalid license plate') {
        this.duplicateLicensePlateError = 'License plate not found';
        // Show vehicle fields when "Invalid license plate" error occurs
        this.showVehicleFields = true;
        this.updateVehicleFieldsValidators();
      } else {
        this.duplicateLicensePlateError = errorMessage;
      }
    } else {
      // Fallback to general error if message doesn't match any specific field
      this.duplicateCustomerError = errorMessage;
    }

    this.existingCustomerData = existingCustomerData;
    this.isSubmitting = false;
  }

  // Called from parent component to clear duplicate customer error
  clearDuplicateCustomerError() {
    this.clearAllDuplicateErrors();
    this.existingCustomerData = null;
  }

  // Helper method to clear all duplicate errors
  private clearAllDuplicateErrors() {
    this.duplicateCustomerError = null;
    this.duplicateEmailError = null;
    this.duplicatePhoneError = null;
    this.duplicateLicensePlateError = null;
  }

  // Update vehicle fields validators based on visibility
  /**
   * Update validators for the 'make', 'model', and 'year' vehicle fields based on visibility and refresh their validity without emitting events.
   * @example
   * updateVehicleFieldsValidators()
   * undefined
   * @param {{void}} {{none}} - No parameters.
   * @returns {{void}} No return value.
   **/
  private updateVehicleFieldsValidators() {
    const makeControl = this.customerForm.get('make');
    const modelControl = this.customerForm.get('model');
    const yearControl = this.customerForm.get('year');

    if (this.showVehicleFields) {
      // Add validators when fields are shown (but not required)
      makeControl?.setValidators([]);
      modelControl?.setValidators([]);
      yearControl?.setValidators([vehicleYearValidator()]);
    } else {
      // Remove validators when fields are hidden
      makeControl?.clearValidators();
      modelControl?.clearValidators();
      yearControl?.clearValidators();
    }

    // Update validity
    makeControl?.updateValueAndValidity({ emitEvent: false });
    modelControl?.updateValueAndValidity({ emitEvent: false });
    yearControl?.updateValueAndValidity({ emitEvent: false });
  }

  // Called from parent component to set field-specific validation errors
  /**
   * Sets a validation error message for a specified field and updates related component state.
   * @example
   * etFieldValidationError('licensePlate', 'Invalid license plate')
   * undefined
   * @param {{string}} {{fieldName}} - The field name ('email', 'phone', or 'licensePlate') to assign the error to.
   * @param {{string}} {{errorMessage}} - The validation error message to set for the specified field.
   * @returns {{void}} No return value.
   **/
  setFieldValidationError(fieldName: string, errorMessage: string) {
    switch (fieldName) {
      case 'email':
        this.duplicateEmailError = errorMessage;
        break;
      case 'phone':
        this.duplicatePhoneError = errorMessage;
        break;
      case 'licensePlate':
        this.duplicateLicensePlateError = errorMessage;
        // Show vehicle fields when "Invalid license plate" error occurs
        if (
          errorMessage === 'Invalid license plate' ||
          errorMessage.toLowerCase().includes('invalid license plate')
        ) {
          this.showVehicleFields = true;
          this.updateVehicleFieldsValidators();
        }
        break;
    }
    this.isSubmitting = false;
  }

  // Method to open customer details modal
  /**
   * Load and display full profile for the currently selected existing customer (if any), updating component state and showing error messages on failure.
   * @example
   * nViewExistingCustomer()
   * undefined
   * @param {{void}} {{none}} - This method does not accept arguments; it uses component state (this.existingCustomerData).
   * @returns {{void}} Updates component state and triggers side effects; no return value.
   **/
  onViewExistingCustomer() {
    if (this.existingCustomerData && this.existingCustomerData.id) {
      this.isLoadingCustomerDetails = true;
      const customerId = this.existingCustomerData.id;

      this.customerService
        .getCustomerProfile(customerId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.isLoadingCustomerDetails = false;
            if (response.status && response.data) {
              // Update existingCustomerData with full customer data from API
              this.existingCustomerData = response.data;
              this.showCustomerDetails = true;
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load customer details. Please try again.',
                life: 3000,
              });
            }
          },
          error: error => {
            console.error('Error fetching customer profile:', error);
            this.isLoadingCustomerDetails = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load customer details. Please try again.',
              life: 3000,
            });
          },
        });
    }
  }

  // Method to close customer details modal
  onCloseCustomerDetails() {
    this.showCustomerDetails = false;
    this.isLoadingCustomerDetails = false;
  }

  get isLicensePlateValid(): boolean {
    const licensePlateControl = this.customerForm.get('licensePlate');
    return licensePlateControl?.valid ?? false;
  }

  /**
   * Return the validation error message for a given form field if it has errors and has been touched or dirty.
   * @example
   * etFieldError('email')
   * 'Invalid email address.'
   * @param {{string}} {{fieldName}} - Name of the form control to evaluate for validation errors.
   * @returns {{string}} Validation error message for the field or an empty string if no visible error.
   **/
  getFieldError(fieldName: string): string {
    const field = this.customerForm.get(fieldName);
    if (field?.errors && (field.touched || field.dirty)) {
      if (field.errors['required']) return `${this.getFieldDisplayName(fieldName)} is required`;
      if (field.errors['maxlength']) {
        const maxLength = field.errors['maxlength'].requiredLength;
        return `${this.getFieldDisplayName(fieldName)} must not exceed ${maxLength} characters`;
      }
      if (field.errors['email']) return 'Invalid email address.';
      if (field.errors['usaPhone']) return field.errors['usaPhone'].message;
      if (field.errors['usaLicensePlate']) return field.errors['usaLicensePlate'].message;
      if (field.errors['vehicleYear']) return field.errors['vehicleYear'].message;
    }
    return '';
  }

  /**
   * Return a user-friendly display name for a given internal field name.
   * @example
   * getFieldDisplayName('firstName')
   * 'First Name'
   * @param {{string}} {{fieldName}} - The internal field name to convert to a display label.
   * @returns {{string}} Return the mapped display name for the provided fieldName, or the original fieldName if no mapping exists.
   **/
  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      email: 'Email',
      licensePlate: 'License Plate',
      make: 'Make',
      model: 'Model',
      year: 'Year',
    };
    return displayNames[fieldName] || fieldName;
  }
  /**
   * Load locations from the API
   */
  private loadLocations(): void {
    this.isLoadingLocations = true;

    // Subscribe to locations from the service
    this.locationSubscription = this.locationService.locations$.subscribe({
      next: locations => {
        this.locationOptions = this.locationService.getLocationOptions();
        this.isLoadingLocations = false;
      },
      error: error => {
        console.error('Error loading locations:', error);
        this.isLoadingLocations = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load locations. Please try again.',
          life: 3000,
        });
      },
    });

    // Load locations if not already loaded
    if (this.locationService.getCurrentLocations().length === 0) {
      this.locationService.loadLocations();
    }
  }

  /**
   * Clean up subscriptions
   */
  ngOnDestroy(): void {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPhoneKeyup() {
    const phoneControl = this.customerForm.get('phone');
    if (phoneControl) {
      phoneControl.markAsTouched();
      phoneControl.updateValueAndValidity();
    }
    // Clear phone validation error when user types
    this.duplicatePhoneError = null;
  }

  onEmailInput() {
    // Clear email validation error when user types
    this.duplicateEmailError = null;
  }

  /**
   * Handle license plate input: clears duplicate error and auto-formats the value to uppercase without spaces or dashes.
   * @example
   * nLicensePlateInput()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} No return value.
   **/
  onLicensePlateInput() {
    // Clear license plate validation error when user types
    this.duplicateLicensePlateError = null;

    // Auto-format license plate to uppercase and remove spaces/dashes
    const licensePlateControl = this.customerForm.get('licensePlate');
    if (licensePlateControl && licensePlateControl.value) {
      const formatted = licensePlateControl.value.toString().toUpperCase().replace(/[\s-]/g, '');
      if (formatted !== licensePlateControl.value) {
        licensePlateControl.setValue(formatted, { emitEvent: false });
      }
    }
  }

  onMakeInput() {
    // Clear license plate validation error when user enters make
    this.duplicateLicensePlateError = null;
  }

  onModelInput() {
    // Clear license plate validation error when user enters model
    this.duplicateLicensePlateError = null;
  }

  /**
   * Clear duplicate license plate error and normalize the 'year' form control to numeric characters only.
   * @example
   * nYearInput()
   * undefined
   * @returns {void} No explicit return value; updates the form control value and component state.
   */
  onYearInput() {
    // Clear license plate validation error when user enters year
    this.duplicateLicensePlateError = null;

    // Auto-format year to numbers only
    const yearControl = this.customerForm.get('year');
    if (yearControl && yearControl.value) {
      const numericOnly = yearControl.value.toString().replace(/\D/g, '');
      if (numericOnly !== yearControl.value) {
        yearControl.setValue(numericOnly, { emitEvent: false });
      }
    }
  }

  /**
   * Handles input events on the first name field: trims the input, clears duplicate-customer errors, and either triggers a name search when the trimmed value has at least 2 characters or clears search results and hides the name dropdown.
   * @example
   * nFirstNameInput(new Event('input'))
   * undefined
   * @param {Event} event - The input event from the first name input element.
   * @returns {void} Does not return a value.
   */
  onFirstNameInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();

    // Clear duplicate errors when user types
    this.duplicateCustomerError = null;

    // Trigger search if value is at least 2 characters
    if (value.length >= 2) {
      this.nameSearchSubject.next(value);
    } else {
      this.nameSearchResults = [];
      this.showNameDropdown = false;
    }
  }

  /**
   * Handle phone input: strip letters/symbols, cap at 10 digits, then search when length >= 3.
   * @example
   * nPhoneInput(event)
   * void
   * @param {Event} event - Input event from the phone number field.
   * @returns {void} No return value; updates component state and may emit a phone search query.
   **/
  onPhoneInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const sanitized = sanitizeCustomerPhoneInput(target.value);
    if (target.value !== sanitized) {
      target.value = sanitized;
    }
    const control = this.customerForm.get('phone');
    if (control && control.value !== sanitized) {
      control.setValue(sanitized, { emitEvent: true });
    }

    // Clear duplicate errors when user types
    this.duplicatePhoneError = null;

    const value = sanitized.trim();

    // Trigger search if value is at least 3 characters
    if (value.length >= 3) {
      this.phoneSearchSubject.next(value);
    } else {
      this.phoneSearchResults = [];
      this.showPhoneDropdown = false;
    }
  }

  searchCustomersByName(name: string): void {
    runCustomerNameSearch(name, this.customerService, this.destroy$, this);
  }

  searchCustomersByPhone(phoneNumber: string): void {
    runCustomerPhoneSearch(phoneNumber, this.customerService, this.destroy$, this);
  }

  /**
   * Select customer from search results
   */
  selectCustomerFromSearch(customer: any): void {
    patchCustomerFormFromSearchResult(this.customerForm, customer, 'phone');
    this.clearAllDuplicateErrors();
    clearCustomerSearchDropdowns(this);
  }

  /**
   * Hide name dropdown when clicking outside
   */
  hideNameDropdown(): void {
    setTimeout(() => {
      this.showNameDropdown = false;
    }, 200);
  }

  /**
   * Hide phone dropdown when clicking outside
   */
  hidePhoneDropdown(): void {
    hideDropdownAfterDelay(() => {
      this.showPhoneDropdown = false;
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
